import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Coins, Package, TrendingUp, CheckCircle2, XCircle, Clock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

function KpiCard({ label, value, sublabel, icon, testId, tone = "indigo" }) {
  const toneClass = {
    indigo: "text-[#4F46E5]",
    emerald: "text-[#059669]",
    slate: "text-[#3F3F46]",
    amber: "text-[#D97706]",
  }[tone];
  return (
    <div className="card-shell p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="data-label">{label}</div>
        <div className={toneClass}>{icon}</div>
      </div>
      <div className="mt-3 font-heading font-black text-4xl tracking-tight">{value}</div>
      {sublabel && <div className="mt-1 text-xs text-[#71717A]">{sublabel}</div>}
    </div>
  );
}

function OverviewTab({ data }) {
  const chartData = [...(data.by_month || [])].reverse().map((m) => ({
    month: m.month,
    Incentive: m.incentive,
    Registrations: m.count,
  }));
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard
          label="Products registered"
          value={data.total_registrations}
          sublabel="All-time count"
          icon={<Package className="w-4 h-4" />}
          testId="agent-kpi-registrations"
          tone="indigo"
        />
        <KpiCard
          label="Total incentive submitted"
          value={`₹${Number(data.total_incentive || 0).toLocaleString("en-IN")}`}
          sublabel="Includes pending approvals"
          icon={<Coins className="w-4 h-4" />}
          testId="agent-kpi-incentive"
          tone="emerald"
        />
        <KpiCard
          label="This month"
          value={data.by_month?.[0] ? `${data.by_month[0].count} regs` : "0 regs"}
          sublabel={data.by_month?.[0] ? `₹${Number(data.by_month[0].incentive || 0).toLocaleString("en-IN")}` : "No registrations yet"}
          icon={<TrendingUp className="w-4 h-4" />}
          testId="agent-kpi-month"
          tone="slate"
        />
      </div>
      <div className="card-shell p-5" data-testid="agent-monthly-chart-card">
        <div className="data-label">Trend</div>
        <h3 className="font-heading font-bold text-lg mt-0.5 mb-4">Monthly incentive &amp; registrations</h3>
        {chartData.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#71717A]">
            No registrations yet. Once you register products via the website, they&apos;ll show up here.
          </div>
        ) : (
          <div style={{ width: "100%", height: 260 }} data-testid="agent-monthly-chart">
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar yAxisId="left" dataKey="Incentive" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="Registrations" fill="#059669" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      <div className="card-shell overflow-hidden">
        <div className="p-5 border-b border-[#E5E5E5]">
          <div className="data-label">Recent registrations</div>
          <h3 className="font-heading font-bold text-lg mt-0.5">Your latest 20 records</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Ticket</th><th>Customer</th><th>Unique ID</th>
                <th>Warranty</th><th>Incentive</th>
                <th className="text-right">Amount</th><th>Registered on</th>
              </tr>
            </thead>
            <tbody data-testid="agent-recent-tbody">
              {(data.recent || []).length === 0 ? (
                <tr><td colSpan={7} className="p-6 text-center text-[#71717A] text-sm">No registrations yet.</td></tr>
              ) : (data.recent || []).map((r) => {
                const d = r.data || {};
                return (
                  <tr key={r.id} data-testid={`agent-record-${r.ticket_id}`}>
                    <td className="font-mono text-xs">{r.ticket_id}</td>
                    <td>{d.full_name || "—"}</td>
                    <td className="font-mono text-xs">{d.unique_product_id || "—"}</td>
                    <td><StatusPill status={r.warranty_approval_status || "pending"} /></td>
                    <td><StatusPill status={r.incentive_approval_status || "pending"} /></td>
                    <td className="text-right font-mono text-[#059669]">₹{Number(r.incentive_amount || 0).toLocaleString("en-IN")}</td>
                    <td className="text-xs text-[#71717A]">{new Date(r.created_at).toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    approved: { bg: "#DCFCE7", text: "#166534", Icon: CheckCircle2 },
    rejected: { bg: "#FEE2E2", text: "#991B1B", Icon: XCircle },
    pending: { bg: "#FEF3C7", text: "#92400E", Icon: Clock },
  };
  const cfg = map[status] || map.pending;
  const Icon = cfg.Icon;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded font-mono"
      style={{ backgroundColor: cfg.bg, color: cfg.text }}>
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
}

function EarningsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/agent/earnings").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-[#71717A] text-sm p-4">Loading…</div>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <KpiCard
          label="Approved & payable"
          value={`₹${Number(data.total_paid || 0).toLocaleString("en-IN")}`}
          sublabel={`${data.count} approved registration${data.count === 1 ? "" : "s"}`}
          icon={<CheckCircle2 className="w-4 h-4" />}
          testId="earnings-total"
          tone="emerald"
        />
        <KpiCard
          label="This month approved"
          value={data.by_month?.[0] ? `₹${Number(data.by_month[0].incentive || 0).toLocaleString("en-IN")}` : "₹0"}
          sublabel={data.by_month?.[0] ? `${data.by_month[0].count} approvals` : "No approvals yet"}
          icon={<Coins className="w-4 h-4" />}
          testId="earnings-month"
          tone="indigo"
        />
      </div>
      <div className="card-shell overflow-hidden">
        <div className="p-5 border-b border-[#E5E5E5]">
          <div className="data-label">Approved payouts</div>
          <h3 className="font-heading font-bold text-lg mt-0.5">Approved incentive history</h3>
          <p className="text-xs text-[#71717A] mt-1">Only registrations approved by the Sales Incentives team appear here.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Ticket</th><th>Customer</th><th>Unique ID</th>
                <th className="text-right">Incentive</th><th>Approved on</th>
              </tr>
            </thead>
            <tbody data-testid="earnings-tbody">
              {(data.recent || []).length === 0 ? (
                <tr><td colSpan={5} className="p-8 text-center text-[#71717A] text-sm">No approved incentives yet.</td></tr>
              ) : (data.recent || []).map((r) => {
                const d = r.data || {};
                return (
                  <tr key={r.id} data-testid={`earning-row-${r.ticket_id}`}>
                    <td className="font-mono text-xs">{r.ticket_id}</td>
                    <td>{d.full_name || "—"}</td>
                    <td className="font-mono text-xs">{d.unique_product_id || "—"}</td>
                    <td className="text-right font-mono text-[#059669] font-medium">₹{Number(r.incentive_amount || 0).toLocaleString("en-IN")}</td>
                    <td className="text-xs text-[#71717A]">{(r.incentive_approval_at || "").slice(0, 10)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function AgentDashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/agent/summary");
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load dashboard");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="p-8 text-red-700">{err}</div>;
  if (!data) return <div className="p-8 text-[#71717A]">Loading…</div>;

  return (
    <div className="p-8 space-y-6">
      <div>
        <div className="data-label mb-1">Salesperson performance</div>
        <h1 className="font-heading font-black text-4xl tracking-tight">
          Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
        </h1>
        <p className="text-sm text-[#71717A] mt-1">
          Your product registrations, pending approvals, and confirmed earnings — all in one place.
        </p>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList data-testid="agent-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="earnings" data-testid="tab-earnings">Earnings</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><OverviewTab data={data} /></TabsContent>
        <TabsContent value="earnings"><EarningsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
